import { Box, Typography, LinearProgress, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";

const steps = [
  {
    state: "JOB_STATE_STARTED",
    label: "Started",
  },
  {
    state: "JOB_STATE_UPLOADING",
    label: "Uploading",
  },
  {
    state: "JOB_STATE_PARTITIONING",
    label: "Partitioning",
  },
  {
    state: "JOB_STATE_PARTITIONED",
    label: "Partitioned",
  },
  {
    state: "JOB_STATE_TASKS_COMPLETED",
    label: "Tasks Completed",
  },
  {
    state: "JOB_STATE_MERGING",
    label: "Merging",
  },
  {
    state: "JOB_STATE_MERGED",
    label: "Merged",
  },
  {
    state: "JOB_STATE_DOWNLOADING",
    label: "Downloading",
  },
  {
    state: "JOB_STATE_COMPLETE",
    label: "Complete",
  },
];

const JobProgress = ({
  state,
  stateTimestamps,
}: {
  state: string;
  stateTimestamps: any;
}) => {
  const [progressPercent, setProgressPercent] = useState(0);
  const GetPercentWithState = (state: string) => {
    const stepIndex: { [key: string]: number } = {
      JOB_STATE_STARTED: 0,
      JOB_STATE_UPLOADING: 12.5,
      JOB_STATE_PARTITIONING: 25,
      JOB_STATE_PARTITIONED: 37.5,
      JOB_STATE_TASKS_COMPLETED: 50,
      JOB_STATE_MERGING: 62.5,
      JOB_STATE_MERGED: 75,
      JOB_STATE_DOWNLOADING: 87.5,
      JOB_STATE_COMPLETE: 100,
    };
    const validStates =
      state === "JOB_STATE_FAILED"
        ? stateTimestamps &&
          Object.entries(stateTimestamps)
            .filter(([key]) => key in stepIndex)
            .sort(
              (a: any, b: any) =>
                new Date(b[1]).getTime() - new Date(a[1]).getTime()
            )
        : state;

    let latestState = "";
    let maxProgress = 0;
    if (Array.isArray(validStates)) {
      for (const [key] of validStates) {
        const progress = stepIndex[key];
        if (progress > maxProgress) {
          maxProgress = progress;
          latestState = key;
        }
      }
    }

    if (
      state === "JOB_STATE_FAILED" &&
      stateTimestamps &&
      stateTimestamps["JOB_STATE_FAILED"] === stateTimestamps[latestState]
    ) {
      return maxProgress;
    }
    if (state === "JOB_STATE_FAILED") {
      return maxProgress + 5;
    }

    return stepIndex[state] ?? 0;
  };

  useEffect(() => {
    const percent = GetPercentWithState(state);

    setProgressPercent(percent);
  }, [state]);
  const failedState = state === "JOB_STATE_FAILED" ? true : false;
  return (
    <Box
      sx={{
        mb: 10,
        borderLeft: "2px solid #B027CE",
        pl: 1,
      }}
    >
      <Typography color="#470056" mb={2}>
        Progress
      </Typography>

      <Box sx={{ position: "relative", mb: 4 }}>
        {/* Failed Job details */}
        {failedState && (
          <Stack
            sx={{
              position: "absolute",
              left: `${progressPercent + 1}%`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                color: "#000271",
                fontWeight: 500,
              }}
            >
              Failed
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "7px",
                color: "#A3A3A3",
                fontStyle: "italic",
                fontWeight: 500,
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              JOB_STATE_FAILED
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                color: "#7A7A7A",
                fontWeight: 500,
                width: "50px",
              }}
            >
              {stateTimestamps && stateTimestamps["JOB_STATE_FAILED"]
                ? formatTimeStamps(stateTimestamps["JOB_STATE_FAILED"])
                : ""}
            </Typography>
          </Stack>
        )}

        {/* failed state showing details */}
        {failedState && (
          <Box
            sx={{
              borderLeft: "2px solid #000271",
              height: "60px",
              ml: `${progressPercent}%`,
              mb: 1,
            }}
          />
        )}

        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={state === "JOB_STATE_FAILED" ? "error" : "primary"}
          sx={{
            width: `100%`,
            height: "10px",
            borderRadius: "10px",
          }}
        />
        {/* Failed state marker */}
        {failedState && (
          <Box
            sx={{
              position: "absolute",
              width: "4px",
              height: "4px",
              top: 74,
              left: `${progressPercent - 0.1}%`,
              backgroundColor: "#fff",
              transform: "rotate(45deg) translate(-50%, -50%)",
              border: "1px solid #fff",
              zIndex: 10,
            }}
          />
        )}

        {steps.map((step, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: failedState ? 70 : 2,
              left:
                i === steps.length - 1
                  ? "99%"
                  : `${(i / (steps.length - 1)) * 100}%`,

              zIndex: 2,
            }}
          >
            {/* Marker */}
            <Box
              sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                border: "1px solid #ffffff",
                backgroundColor: "#fff",
              }}
            />

            <Box
              sx={{
                // mt: "10px",
                ml: "2px",
                borderLeft: "2px solid #000271",
                height: "40px",
                position: "absolute",
                top: 10,
              }}
            />
            <Box
              sx={{
                top: 30,
                left: 8,
                position: "absolute",
              }}
            >
              <Stack>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "12px",
                    color: "#000271",
                    fontWeight: 500,
                  }}
                >
                  {step?.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "7px",
                    color: "#A3A3A3",
                    fontStyle: "italic",
                    fontWeight: 500,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {step?.state}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "10px",
                    color: "#7A7A7A",
                    fontWeight: 500,
                  }}
                >
                  {stateTimestamps && stateTimestamps[step?.state]
                    ? formatTimeStamps(stateTimestamps[step?.state])
                    : ""}
                </Typography>
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default JobProgress;
const formatTimeStamps = (date: string) => {
  const newDate = new Date(date);
  const year = newDate.getFullYear();
  const month = (newDate.getMonth() + 1).toString().padStart(2, "0");
  const day = newDate.getDate().toString().padStart(2, "0");
  const hour = newDate.getHours().toString().padStart(2, "0");
  const min = newDate.getMinutes().toString().padStart(2, "0");
  const sec = newDate.getSeconds().toString().padStart(2, "0");

  return `${day}/${month}/${year}
  ${hour}:${min}:${sec}
  `;
};
